'use client';

// Hooks that surface user-preference state from the network store.
//
// For SDK / RPC / banner / NetworkLink decisions, do NOT use this file —
// use `useNetworkMode()` from `./use-network-mode.ts` instead. That hook
// is gate-clamped (returns `'devnet'` whenever `mainnet_enabled` is
// off) and is the single source of truth for "which network is the
// user on?".
//
// The hook below (`useUserNetwork`) returns the RAW preference value —
// it doesn't apply the gate. Use it only for the wallet-toggle UI's
// "currently checked" state and for anything that needs to know the
// raw cookie value (e.g. share-button URL builders).
import { c as _c } from "react/compiler-runtime";
import { clearRelayFeePayerCache } from '@morfi/backend-sdk-v2/client';
import { teardownImpersonation } from '@/lib/impersonation/teardown';
import { isToggleEnabled } from './config';
import { useNetworkStore } from './store';
import type { Network } from './types';
export interface UseUserNetworkResult {
  /** Raw preference — can be `'mainnet'` even when the gate is off.
   *  Hydrates from the `NETWORK_PREFERENCE` cookie on mount. */
  network: Network;
  /** Persists the preference to the cookie + Zustand store. Awaits any
   *  active engineer-impersonation teardown FIRST so the grant token
   *  is revoked / cleared before the SDK re-binds against the new
   *  network (FR-029, FR-068 — grants are per-network and must not
   *  survive a toggle). The caller is responsible for any SDK re-init
   *  or URL change that needs to happen on toggle (the wallet toggle
   *  uses `useSwitchNetwork()` which wraps this and adds the URL
   *  flip). */
  setUserNetwork: (next: Network) => Promise<void>;
  /** True when the deployment ships both network configs. The toggle
   *  UI is hidden when false — only one network is reachable. */
  toggleEnabled: boolean;
}
export function useUserNetwork() {
  const $ = _c(6);
  const network = useNetworkStore(_temp);
  const storeSetUserNetwork = useNetworkStore(_temp2);
  let t0;
  if ($[0] !== storeSetUserNetwork) {
    t0 = async function setUserNetwork(next) {
      await teardownImpersonation("network_toggle");
      clearRelayFeePayerCache();
      storeSetUserNetwork(next);
    };
    $[0] = storeSetUserNetwork;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setUserNetwork = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isToggleEnabled();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== network || $[4] !== setUserNetwork) {
    t2 = {
      network,
      setUserNetwork,
      toggleEnabled: t1
    };
    $[3] = network;
    $[4] = setUserNetwork;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp2(s_0) {
  return s_0.setUserNetwork;
}
function _temp(s) {
  return s.current;
}